import { apiFormaleta, SinRed, type AltaPiezas, type AltaResultado } from '@/api/formaleta';
import { comprimirFoto } from './comprimirFoto';
import { subirFotoFormaleta } from './subirFoto';

/**
 * F-6 · LA COLA OFFLINE. Un alta que no pudo salir no se pierde: se guarda en
 * el teléfono y sale cuando vuelve la señal.
 *
 * ── Por qué existe ──────────────────────────────────────────────────────────
 * El inventario se hace en obra, de pie, frente a una pila de cuarenta paneles.
 * Ahí la señal va y viene. Sin cola, un alta que falla por red deja al operario
 * con una pieza medida, fotografiada y ubicada, y nada que mostrar: el trabajo
 * se repite entero. Con cola, el gesto termina igual que siempre.
 *
 * ── La decisión que define esta pieza (Daniel, 5-nov-2026) ──────────────────
 * `FormPieza.Consecutivo` es una columna COMPUTADA sobre el IDENTITY: **solo la
 * base puede emitir el código** (`AD-00005`). Y ese código es justamente lo que
 * el operario graba en el aluminio. O sea que offline NO HAY CÓDIGO QUE GRABAR,
 * y no es un detalle de implementación: es el acto físico que vuelve a la pieza
 * identificable para siempre.
 *
 * De los tres caminos posibles Daniel eligió **no grabar hasta que sincronice**.
 * Es el único que no inventa identidad: la pieza entra sin marca, y cuando
 * vuelve la señal aparece su consecutivo real. Se apoya en algo que ya existía
 * desde F-1 por otra razón —el check «ya le pegué la marca», que separa guardar
 * de marcar porque la grabadora puede estar a diez minutos de distancia—. Las
 * otras dos opciones (reservar bloques de consecutivos, o grabar un código
 * local) exigían esquema nuevo o rompían la premisa de que el código grabado ES
 * el consecutivo.
 *
 * ⚠️ CONSECUENCIA QUE HAY QUE RESPETAR: una pieza encolada NO tiene código.
 * Cualquier vista que muestre lo pendiente debe decirlo con esas palabras y
 * NUNCA mostrar un número provisional; un `AD-` falso en el aluminio es un
 * error que después no se puede distinguir de uno real.
 *
 * ── Lo que esta pieza NO hace: service worker ──────────────────────────────
 * Hay `manifest.json` pero NO hay service worker, así que con el teléfono sin
 * señal **la app no abre**: el navegador no tiene el shell cacheado. Entonces
 * esta cola cubre un caso concreto y hay que ser honesto sobre cuál:
 *
 *   ✅ el operario abrió la app con señal y la señal se cayó mientras trabaja
 *   ❌ el operario llega a la obra ya sin señal y abre la app desde cero
 *
 * Daniel decidió el 5-nov-2026 NO hacer el service worker todavía, y la razón
 * es buena: registrarlo afecta a TODA ArquiFlow (tareas, trámites, Gantt), no
 * solo a formaleta, y la intención a futuro es una **app dedicada a Formaleta**,
 * que es donde ese service worker va a vivir sin arrastrar riesgo ajeno. Queda
 * anotado en la guía del proyecto como el trabajo que completa el caso ❌.
 *
 * ── Por qué IndexedDB nativo y no `idb`/`dexie` ────────────────────────────
 * Son tres operaciones (agregar, listar, borrar) sobre un solo store. La
 * dependencia ahorraría ~30 líneas y agrega una entrada al bundle de una app
 * que además se va a mudar de repo. No vale el cambio.
 */

const BASE_DATOS = 'arqui-flow-formaleta';
const VERSION = 1;
const TIENDA = 'altas-pendientes';

/** Después de esto la pieza deja de reintentarse sola y pide ojo humano. */
const INTENTOS_MAX = 3;

export interface AltaEncolada {
  /** Lo pone IndexedDB. Es la clave y además el ORDEN: sale primero la primera. */
  id: number;
  creadoEn: number;
  /** El alta tal cual se habría enviado, con el GPS del momento en que se midió. */
  payload: AltaPiezas;
  /** Ya comprimida (~250 KB). Guardar los 4 MB de la cámara llenaría la cuota. */
  foto: Blob | null;
  /**
   * Si el modelo ya tenía foto cuando se encoló. Decide si al sincronizar la
   * foto va al renglón o a la pieza, replicando la regla de `PanelAlta`.
   */
  modeloTeniaFoto: boolean;
  /** Para describir la pieza en la lista de pendientes, que no tiene código. */
  etiqueta: string;
  intentos: number;
  ultimoError: string | null;
}

/** Navegador sin IndexedDB (modo privado viejo): la cola se apaga, no revienta. */
export function colaDisponible(): boolean {
  return typeof indexedDB !== 'undefined';
}

function abrir(): Promise<IDBDatabase> {
  return new Promise((ok, falla) => {
    const req = indexedDB.open(BASE_DATOS, VERSION);
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(TIENDA)) {
        db.createObjectStore(TIENDA, { keyPath: 'id', autoIncrement: true });
      }
    };
    req.onsuccess = () => ok(req.result);
    req.onerror = () => falla(req.error ?? new Error('no se pudo abrir la cola'));
  });
}

/** Envuelve una transacción y espera a que COMMITEE, no a que la request vuelva. */
async function enTienda<T>(
  modo: IDBTransactionMode,
  trabajo: (t: IDBObjectStore) => IDBRequest<T>,
): Promise<T> {
  const db = await abrir();
  try {
    return await new Promise<T>((ok, falla) => {
      const tx = db.transaction(TIENDA, modo);
      const req = trabajo(tx.objectStore(TIENDA));
      // `oncomplete` y no `req.onsuccess`: en una escritura el éxito de la
      // request todavía no garantiza que el dato quedó en disco.
      tx.oncomplete = () => ok(req.result);
      tx.onerror = () => falla(tx.error ?? new Error('falló la cola'));
      tx.onabort = () => falla(tx.error ?? new Error('transacción abortada'));
    });
  } finally {
    db.close();
  }
}

/**
 * Guarda el alta en el teléfono. Comprime la foto ANTES de guardarla: a 4 MB
 * por pieza una tanda de cuarenta serían 160 MB y el navegador cortaría la
 * cuota a mitad de la pila; a ~250 KB la misma tanda son ~10 MB.
 */
export async function encolarAlta(entrada: {
  payload: AltaPiezas;
  foto: Blob | null;
  modeloTeniaFoto: boolean;
  etiqueta: string;
}): Promise<void> {
  const foto = entrada.foto ? (await comprimirFoto(entrada.foto)).archivo : null;
  await enTienda('readwrite', t => t.add({
    creadoEn: Date.now(),
    payload: entrada.payload,
    foto,
    modeloTeniaFoto: entrada.modeloTeniaFoto,
    etiqueta: entrada.etiqueta,
    intentos: 0,
    ultimoError: null,
  }));
}

export async function listarPendientes(): Promise<AltaEncolada[]> {
  if (!colaDisponible()) return [];
  try {
    const todas = await enTienda<AltaEncolada[]>('readonly', t => t.getAll());
    return todas.sort((a, b) => a.id - b.id);
  } catch {
    return [];
  }
}

export async function contarPendientes(): Promise<number> {
  if (!colaDisponible()) return 0;
  try {
    return await enTienda<number>('readonly', t => t.count());
  } catch {
    return 0;
  }
}

/** Para descartar a mano una pieza que quedó trabada con un rechazo definitivo. */
export async function descartarPendiente(id: number): Promise<void> {
  await enTienda('readwrite', t => t.delete(id));
}

export interface ResultadoDrenaje {
  /** Altas que entraron a la base en esta pasada. */
  subidas: number;
  /** Rechazadas por el servidor (no por la red): quedan con su error a la vista. */
  rechazadas: number;
  quedanPendientes: number;
  /** Se cortó porque volvió a caerse la red. No es un error: es esperar. */
  cortadaPorRed: boolean;
  /** Fotos que no subieron, con la pieza SÍ guardada. Nunca invalida el alta. */
  fotosFallidas: number;
}

/**
 * Vacía la cola en ORDEN. Se usa al reconectar y desde el botón del listado.
 *
 * Las tres reacciones posibles, que es donde está toda la lógica:
 *
 *  · **Entró** → se borra del teléfono. Única forma de salir de la cola.
 *  · **El servidor la rechazó** (400 porque el estado ya no existe, 409…) → se
 *    anota el error y se SIGUE con la próxima. Reintentar un rechazo definitivo
 *    es un loop, y peor: taparía a las piezas buenas que vienen detrás.
 *  · **No hubo respuesta** (`SinRed`) → se CORTA la pasada entera y no se toca
 *    nada. Si la red se volvió a caer, insistir con las demás solo gasta
 *    batería, y cortar preserva el orden de la pila.
 *
 * La foto se sube después del alta —antes no hay fila a la que colgarla— y su
 * falla nunca convierte una pieza guardada en un error, igual que en el alta
 * en vivo.
 */
export async function drenarCola(
  alAvanzar?: (hechas: number, total: number) => void,
): Promise<ResultadoDrenaje> {
  const cola = await listarPendientes();
  const r: ResultadoDrenaje = {
    subidas: 0, rechazadas: 0, quedanPendientes: cola.length,
    cortadaPorRed: false, fotosFallidas: 0,
  };

  for (const [i, item] of cola.entries()) {
    let alta: AltaResultado;
    try {
      alta = await apiFormaleta.inventariar(item.payload);
    } catch (e) {
      if (e instanceof SinRed) {
        r.cortadaPorRed = true;
        break;
      }
      const intentos = item.intentos + 1;
      await enTienda('readwrite', t => t.put({
        ...item, intentos, ultimoError: (e as Error).message,
      }));
      r.rechazadas += 1;
      continue;
    }

    if (item.foto) {
      try {
        // Misma regla que el alta en vivo: al RENGLÓN cuando el modelo todavía
        // no tiene foto, porque así sirve para todas las piezas del modelo.
        const alRenglon = !item.modeloTeniaFoto || alta.piezas.length === 0;
        await subirFotoFormaleta(
          alRenglon ? 'renglon' : 'pieza',
          alRenglon ? alta.idForm : alta.piezas[0]!.id,
          item.foto,
        );
      } catch {
        // La pieza YA está en la base: su alta no se puede deshacer por la foto.
        r.fotosFallidas += 1;
      }
    }

    await descartarPendiente(item.id);
    r.subidas += 1;
    alAvanzar?.(r.subidas, cola.length);
  }

  r.quedanPendientes = await contarPendientes();
  return r;
}

/** Las que ya agotaron los reintentos: necesitan que alguien las mire. */
export function estaTrabada(item: AltaEncolada): boolean {
  return item.intentos >= INTENTOS_MAX;
}
